import { useId, useState, type ReactNode } from 'react'
import { useOptionalT } from '../i18n/LocaleProvider'

export interface DonateAmountPickerProps {
  /** Preset amounts in whole currency units, e.g. `[10, 30, 50, 100]`. A custom box follows. */
  amounts: number[]
  /** Index of the amount checked on first render. The source pre-checks the first. */
  defaultIndex?: number
  /**
   * `form` — `index.html`, where `.ul-donate-form` is itself the `<form>`.
   * `div`  — `donation-details.html`, where it is a `<div>` inside an outer `<form>`.
   * Nesting a form inside a form is invalid HTML, which is why this is a prop rather
   * than always rendering a `<form>`.
   */
  as?: 'form' | 'div'
  className?: string
  customPlaceholder?: string
  /** Trailing content inside the picker — the submit button on `index.html`. */
  children?: ReactNode
  onChange?: (amount: number | null) => void
}

/**
 * The `.ul-donate-form` amount picker. Used on the Home and Donation Details pages,
 * which share identical option markup.
 *
 * Behaviour:
 *  - selecting a preset writes that amount (without the `$`) into the custom input;
 *  - focusing the custom input leaves the last selected preset checked rather than
 *    clearing it, and **typing** in it selects the custom amount — so the chip that is lit
 *    is always the amount that is reported.
 *
 * Two markup details the CSS depends on — do not "tidy" either:
 *  - the five preset radios are `hidden`; the custom-amount radio is **not**;
 *  - the custom wrapper's `<label for>` wraps the number input that carries that same id.
 *
 * **Every id is scoped to the instance** (2026-09-25). They were the literals
 * `donate-amount-1` … and `donate-amount-custom` until then, and `/donations` renders two
 * pickers — the appeal's and the one in the band beneath it — so a click on the lower one's
 * `$30` label checked the *upper* one's radio, the first element in the document with that
 * id. Harmless while neither form sent anything; not once the appeal's form posts the
 * amount it holds. No stylesheet selects on the ids (checked), only on the classes.
 */
export function DonateAmountPicker({
  amounts,
  defaultIndex = 0,
  as = 'form',
  className,
  customPlaceholder: customPlaceholderProp,
  children,
  onChange,
}: DonateAmountPickerProps) {
  const t = useOptionalT()
  const uid = useId()
  const customPlaceholder = customPlaceholderProp ?? t('ul.donate.customPlaceholder')
  const [selected, setSelected] = useState<number | null>(defaultIndex)
  const [custom, setCustom] = useState(() => {
    const initial = amounts[defaultIndex]
    return initial === undefined ? '' : String(initial)
  })

  const pick = (i: number) => {
    setSelected(i)
    const amount = amounts[i]
    if (amount !== undefined) {
      setCustom(String(amount))
      onChange?.(amount)
    }
  }

  const classes = className ? `ul-donate-form ${className}` : 'ul-donate-form'

  const body = (
    <>
      {amounts.map((amount, i) => (
        <div key={amount}>
          <input
            type="radio"
            name="donate-amount"
            id={`${uid}-amount-${i + 1}`}
            checked={selected === i}
            onChange={() => pick(i)}
            hidden
          />
          <label htmlFor={`${uid}-amount-${i + 1}`} className="ul-donate-form-label">
            ${amount}
          </label>
        </div>
      ))}

      <div className="custom-amount-wrapper">
        <input
          type="radio"
          name="donate-amount"
          id={`${uid}-custom-radio`}
          checked={selected === null}
          onChange={() => {
            setSelected(null)
            onChange?.(custom === '' ? null : Number(custom))
          }}
        />
        <label htmlFor={`${uid}-custom`} className="ul-donate-form-label">
          <input
            type="number"
            name="custom-amount"
            id={`${uid}-custom`}
            placeholder={customPlaceholder}
            className="ul-donate-form-custom-input"
            value={custom}
            onChange={(e) => {
              setCustom(e.target.value)
              // Typing an amount chooses it. Until 2026-09-25 the preset stayed checked and
              // highlighted while `onChange` reported the typed figure, so the chip said $10
              // and the form sent $250 — harmless while no form sent anything.
              setSelected(null)
              onChange?.(e.target.value === '' ? null : Number(e.target.value))
            }}
          />
        </label>
      </div>

      {children}
    </>
  )

  if (as === 'div') return <div className={classes}>{body}</div>

  return (
    <form action="#" className={classes} onSubmit={(e) => e.preventDefault()}>
      {body}
    </form>
  )
}
